import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dispatch, SetStateAction, useContext } from 'react';
import { checkActionSubPermission, toTypeCase } from '../../../../../utils/common';
import MlxActionIcon from '../../../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../../../components/common/MlxInputSwitch/MlxInputSwitch';
import { QueryClient, useMutation, UseQueryResult } from 'react-query';
import { _DatatablePageEvent, _Venue } from '../../../../../constants/staticTypes';
import logger from '../../../../../utils/logger';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxConfirmPopup from '../../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { confirmPopup } from 'primereact/confirmpopup';
import { CONFIRM_VENUE_USERS_DELETE, CONFIRM_VENUE_USERS_STATUS_CHANGE } from '../../../../../constants/strings';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { AxiosResponse } from 'axios';
import {
    _AddUserVenueFormType,
    deleteVenueUserDetailByID,
    getVenueUserDetailByID,
    updateVenueUserStatus,
} from '../../../../../helpers/venueUsersHelper';
import { OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../../constants/constants';
import { AuthCtx } from '../../../../../context/AuthCtxProvider';
import MlxToolTip from '../../../../../components/common/MlxToolTip/MlxToolTip';

type Props = {
    dataTableEvent?: _DatatablePageEvent;
    setDataTableEvent?: Dispatch<SetStateAction<_DatatablePageEvent>>;
    openEditVenueSidebar: (data: _AddUserVenueFormType) => void;
    venueUsersListData: _Venue[];
    setVenueUsersListData: Dispatch<SetStateAction<_Venue[]>>;
    venueUsersListQuery: UseQueryResult<AxiosResponse<any, any>, unknown>;
    selectedVenueOption?: string;
    parent?: 'copyUsers';
    selectedProductsForBulkCopy?: any[];
    setSelectedProductsForBulkCopy?: Dispatch<SetStateAction<any[]>>;
    isVenueListLoading?: boolean;
    selectedCopyVenueOption?: string;
    copyType?: 'copy-to' | 'copy-from' | '';
};

const languageTexts = {
    CONFIRM_VENUE_USERS_DELETE: CONFIRM_VENUE_USERS_DELETE,
    CONFIRM_VENUE_USERS_STATUS_CHANGE: CONFIRM_VENUE_USERS_STATUS_CHANGE,
};

function VenueUsersList({
    dataTableEvent,
    setDataTableEvent,
    openEditVenueSidebar,
    venueUsersListData,
    setVenueUsersListData,
    venueUsersListQuery,
    selectedVenueOption,
    parent,
    selectedProductsForBulkCopy,
    setSelectedProductsForBulkCopy,
    isVenueListLoading,
    selectedCopyVenueOption,
    copyType,
}: Readonly<Props>) {
    const translatedText = useTranslation(languageTexts, 'none');
    const getVenueUserByIDMutation = useMutation(HttpServiceHelper);
    const queryClient = new QueryClient();
    const { userType } = useContext(AuthCtx);

    const handleVenueUserStatusChange = (rowData: any, event: any) => {
        updateVenueUserStatus(
            getVenueUserByIDMutation,
            { id: rowData?.id, newStatus: !event.target.checked },
            (data) => {
                // Update the isActive field in the state
                setVenueUsersListData((prevData: any) =>
                    prevData.map((staff: any) => (staff.id === rowData.id ? { ...staff, isActive: !staff.isActive } : staff))
                );
            },
            (error) => logger.log('Failed to update the status', error),
            selectedVenueOption
        );
    };

    const confirmStatusChange = (event: any, rowData: any) => {
        confirmPopup({
            target: event.target,
            message: translatedText.CONFIRM_VENUE_USERS_STATUS_CHANGE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => {
                handleVenueUserStatusChange(rowData, event);
            },
            reject: () => {},
        });
    };

    const statusTemplateWithAction = (rowData: any) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{toTypeCase(rowData.isActive ? 'Active' : 'Inactive', 'capitalize')}</span>
                <MlxInputSwitch
                    name="admin-staff-status"
                    checked={rowData.isActive}
                    onClick={(event) => confirmStatusChange(event, rowData)}
                />
            </div>
        );
    };

    const handleVenueUserEdit = (rowData: any) => {
        getVenueUserDetailByID(
            getVenueUserByIDMutation,
            { id: rowData?.id },
            (data) => {
                let venueUserData = data?.data;
                const venueUserEditableDataForm: _AddUserVenueFormType = {
                    id: venueUserData.id,
                    loginUserName: venueUserData.loginUserName,
                    password: '',
                    preferredName: venueUserData.preferredName,
                    firstName: venueUserData.firstName,
                    lastName: venueUserData.lastName,
                    email: venueUserData.email,
                    phoneNumber: venueUserData.phoneNumber,
                    hireDate: venueUserData.hireDate,
                    staffPermissionRoles: venueUserData.staffPermissionRoles[0]?.permissionRoleId,
                    photoKey: venueUserData.photoKey,
                    sendPasswordCreation: true,
                    passwordLessLoginOnly: true,
                    sendPasswordCreationTo: 'email',
                    userType: venueUserData.userType,
                    signedUrl: venueUserData.signedUrl,
                    jobRoles: venueUserData.jobRoles?.map((item: any) => {
                        return item.jobRoleId;
                    }),
                    posNumber: venueUserData.posNumber,
                };
                openEditVenueSidebar(venueUserEditableDataForm);
            },
            (error) => logger.log('Failed to fetch user details', error),
            selectedVenueOption
        );
    };

    const handleVenueUserDelete = (rowData: any) => {
        deleteVenueUserDetailByID(
            getVenueUserByIDMutation,
            { id: rowData?.id },
            () => {
                venueUsersListQuery.refetch();
            },
            (error) => logger.log('Failed to delete admin staff', error),
            selectedVenueOption
        );
    };

    const confirmDelete = (event: any, rowData: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: translatedText.CONFIRM_VENUE_USERS_DELETE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => {
                handleVenueUserDelete(rowData);
            },
            reject: () => {},
        });
    };

    const actionTemplate = (rowData: any) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE_STAFF, OrgSubPermissionKeys.EDIT_VENUE_STAFF)) && (
                    <MlxActionIcon className="me-3 edit-icon-selection" actionType="EDIT" onClick={() => handleVenueUserEdit(rowData)} />
                )}
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(
                        queryClient,
                        OrgPermissionKeys.MANAGE_VENUE_STAFF,
                        OrgSubPermissionKeys.DELETE_VENUE_STAFF
                    )) && (
                    <MlxActionIcon
                        className="me-3 delete-icon-selection"
                        actionType="DELETE"
                        onClick={(event) => confirmDelete(event, rowData)}
                    />
                )}
            </div>
        );
    };

    const jobRolesTemplate = (rowData: any) => {
        return <span>{rowData.jobRoles?.map((item: any) => item.jobRoleName).join(', ')}</span>;
    };

    const isSelectable = (data: any) => {
        const otherTenants = data.activeTenants.map((tenant: any) => tenant.id);

        //if the user is present in the other venue, then return true for disabling selection
        if (selectedCopyVenueOption && otherTenants.includes(copyType === 'copy-to' ? selectedCopyVenueOption : selectedVenueOption)) {
            return false;
        } else {
            return true;
        }
    };

    const isRowSelectable = (event: any) => {
        if (event.data) {
            isSelectable(event.data);
        }
        return true;
    };

    const rowClassName = (data: any) => (isSelectable(data) ? '' : 'p-disabled');

    return (
        <MlxBlockUI isLoading={getVenueUserByIDMutation.isLoading || venueUsersListQuery.isRefetching || isVenueListLoading} opacity={0.5}>
            <MlxConfirmPopup />
            <DataTable
                className="custom-p-table"
                value={venueUsersListData}
                paginator={parent !== 'copyUsers'}
                rows={parent === 'copyUsers' ? undefined : dataTableEvent?.rows}
                totalRecords={dataTableEvent?.totalRows}
                first={dataTableEvent?.first}
                rowsPerPageOptions={dataTableEvent?.rowsPerPageOptions}
                size="small"
                removableSort
                scrollable
                scrollHeight="flex"
                paginatorTemplate={dataTableEvent?.paginatorTemplate}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                dataKey="id"
                selectionMode={parent === 'copyUsers' ? 'checkbox' : null}
                selection={selectedProductsForBulkCopy ?? []}
                onSelectionChange={(e: any) => (setSelectedProductsForBulkCopy ? setSelectedProductsForBulkCopy(e.value) : undefined)}
                onPage={(event) => {
                    if (!dataTableEvent) return;
                    if (setDataTableEvent) {
                        setDataTableEvent({
                            ...dataTableEvent,
                            ...event,
                            forceUpdate: dataTableEvent.forceUpdate + 1,
                            rowsPerPageOptions: dataTableEvent.rowsPerPageOptions ?? [],
                            paginatorTemplate: dataTableEvent.paginatorTemplate ?? '',
                            search: dataTableEvent.search ?? '',
                            totalRows: dataTableEvent.totalRows ?? 0,
                        });
                    }
                }}
                isDataSelectable={parent === 'copyUsers' ? (e) => isRowSelectable(e) : undefined}
                rowClassName={parent === 'copyUsers' ? rowClassName : undefined}
                lazy
            >
                {parent === 'copyUsers' && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>}
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    header="Name"
                    body={(rowData) => `${rowData.firstName} ${rowData.lastName}`}
                ></Column>
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    header="Email"
                    field="email"
                    body={(rowData) => (
                        <MlxToolTip target={`.staffEmail-${rowData.id}`}>
                            <div
                                className={`staffEmail-${rowData.id} overflow-ellipsis`}
                                data-pr-tooltip={rowData?.email}
                                data-pr-position="bottom"
                            >
                                {rowData?.email}
                            </div>
                        </MlxToolTip>
                    )}
                ></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Role" body={jobRolesTemplate}></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Phone" field="phoneNumber"></Column>
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    header="Hire Date"
                    body={(rowData) => (rowData.hireDate ? new Date(rowData.hireDate).toLocaleDateString() : '')}
                ></Column>
                {parent !== 'copyUsers' && <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplateWithAction}></Column>}
                {parent !== 'copyUsers' &&
                    (userType === 'organizationAdmin' ||
                        checkActionSubPermission(
                            queryClient,
                            OrgPermissionKeys.MANAGE_VENUE_STAFF,
                            OrgSubPermissionKeys.EDIT_VENUE_STAFF
                        ) ||
                        checkActionSubPermission(
                            queryClient,
                            OrgPermissionKeys.MANAGE_VENUE_STAFF,
                            OrgSubPermissionKeys.DELETE_VENUE_STAFF
                        )) && <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>}
            </DataTable>
        </MlxBlockUI>
    );
}

export default VenueUsersList;
